/**
 * ProductsContent - Gestão de Produtos (catálogo, imagens, IA, importação em lote).
 * Extraído do App.js (Fase 2 refactor) — mesmo comportamento, apenas reorganização.
 * COLOR_OPTIONS movido junto (era usado apenas por este componente).
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Camera, Clock, Download, Edit, Eye, EyeOff, FileText, Filter,
  Image as ImageIcon, Mic, MoreVertical, Package, PlusCircle, Sparkles, Star, Trash2, Upload
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';
import ProductModal from './ProductModal';
import MenuAIModal from './MenuAIModal';
import AISuggestionsModal from './AISuggestionsModal';
import AutoLinkModal from './AutoLinkModal';
import CreateCategoryModal from './CreateCategoryModal';
import GalleryModal from './GalleryModal';
import ImportModal from './ImportModal';
import VoiceConfirmModal from './VoiceConfirmModal';
import AIImageModal from './AIImageModal';
import BatchAIModal from './BatchAIModal';
import ProductsToolbar from './products/ProductsToolbar';
import ProductsGrid from './products/ProductsGrid';
import { getBackendUrl } from '../../utils/api';

const API = `${getBackendUrl()}/api`;

const COLOR_OPTIONS = [
  { value: '#ef4444', label: 'Vermelho', color: '#ef4444' },
  { value: '#f59e0b', label: 'Laranja', color: '#f59e0b' },
  { value: '#eab308', label: 'Amarelo', color: '#eab308' },
  { value: '#22c55e', label: 'Verde', color: '#22c55e' },
  { value: '#06b6d4', label: 'Ciano', color: '#06b6d4' },
  { value: '#3b82f6', label: 'Azul', color: '#3b82f6' },
  { value: '#8b5cf6', label: 'Roxo', color: '#8b5cf6' },
  { value: '#ec4899', label: 'Rosa', color: '#ec4899' },
  { value: '#6b7280', label: 'Cinza', color: '#6b7280' }
];

const ProductsContent = () => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [additionalGroups, setAdditionalGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [isCreateCategoryModalOpen, setIsCreateCategoryModalOpen] = useState(false);
  const [creatingCategory, setCreatingCategory] = useState(false);
  const [showProductImages, setShowProductImages] = useState(false); // Toggle para mostrar/ocultar imagens
  
  // Estados para galeria de imagens
  const [showAutoLinkModal, setShowAutoLinkModal] = useState(false);
  const [autoLinkLoading, setAutoLinkLoading] = useState(false);
  const [autoLinkResult, setAutoLinkResult] = useState(null);
  const [showGalleryModal, setShowGalleryModal] = useState(false);
  const [galleryImages, setGalleryImages] = useState([]);
  const [galleryCategories, setGalleryCategories] = useState([]);
  const [galleryFilter, setGalleryFilter] = useState('');
  const [gallerySearch, setGallerySearch] = useState('');
  const [loadingGallery, setLoadingGallery] = useState(false);
  
  // Estados para sugestões IA
  const [showAISuggestionsModal, setShowAISuggestionsModal] = useState(false);
  const [aiSuggestionsLoading, setAiSuggestionsLoading] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState({});
  const [applyingSuggestions, setApplyingSuggestions] = useState(false);
  
  // Estados para Montar Cardápio com IA (análise de imagem)
  const [showMenuAIModal, setShowMenuAIModal] = useState(false);
  const [menuAIImages, setMenuAIImages] = useState([]);
  const [menuAIAnalyzing, setMenuAIAnalyzing] = useState(false);
  const [menuAIResult, setMenuAIResult] = useState(null);
  const [menuAISaving, setMenuAISaving] = useState(false);
  
  // Estados para Comandos de Voz/Texto
  const [voiceCommand, setVoiceCommand] = useState('');
  const [voiceCommandLoading, setVoiceCommandLoading] = useState(false);
  const [voiceCommandResult, setVoiceCommandResult] = useState(null);
  const [showVoiceConfirmModal, setShowVoiceConfirmModal] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  
  // Estados para Geração de Imagem com IA
  const [showAIImageModal, setShowAIImageModal] = useState(false);
  const [aiImageLoading, setAiImageLoading] = useState(false);
  const [aiImageResult, setAiImageResult] = useState(null);
  const [aiImageConfig, setAiImageConfig] = useState({
    emergent_key: localStorage.getItem('emergent_image_key') || '',
    photo_style: 'amateur',
    environment: 'auto',
    extra_details: '',
    use_logo: false,
    remember_config: true
  });
  
  // Estados para Geração em Lote
  const [showBatchAIModal, setShowBatchAIModal] = useState(false);
  const [batchProducts, setBatchProducts] = useState([]);
  const [batchProgress, setBatchProgress] = useState({ current: 0, total: 0, status: 'idle' }); // idle, running, done
  const [batchResults, setBatchResults] = useState([]); // {productId, name, success, image_url, error}
  const [batchLoading, setBatchLoading] = useState(false);
  
  const [newCategoryData, setNewCategoryData] = useState({
    name: '',
    description: '',
    color: '#3b82f6'
  });
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    image_url: '',
    available: true,
    preparation_time: '',
    additional_groups: [],
    display_order: '',
    track_stock: false,
    stock_quantity: '',
    low_stock_threshold: '',
    stock_unit: 'un'
  });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState('all');
  const [imagePreview, setImagePreview] = useState(null);
  const [creatingDemo, setCreatingDemo] = useState(false);

  // Estados para criação rápida de Grupo de Adicionais (dentro do modal de produto)
  const [showQuickGroup, setShowQuickGroup] = useState(false);
  const [savingQuickGroup, setSavingQuickGroup] = useState(false);
  const [quickGroup, setQuickGroup] = useState({ name: '', min_choices: 0, max_choices: 1, is_required: false });
  const [quickGroupItems, setQuickGroupItems] = useState([{ name: '', price: '' }]);

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchAdditionalGroups();
  }, []);

  // Reseta o painel de criação rápida de grupo quando o modal de produto fecha
  useEffect(() => {
    if (!isModalOpen) {
      setShowQuickGroup(false);
      setSavingQuickGroup(false);
      setQuickGroup({ name: '', min_choices: 0, max_choices: 1, is_required: false });
      setQuickGroupItems([{ name: '', price: '' }]);
    }
  }, [isModalOpen]);

  const fetchProducts = async () => {
    try {
      // Cache-busting header to ensure fresh data after group linking operations
      const response = await axios.get(`${API}/products`, {
        headers: { 'Cache-Control': 'no-cache' }
      });
      setProducts(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchAdditionalGroups = async () => {
    try {
      const response = await axios.get(`${API}/additional-groups`);
      setAdditionalGroups(response.data);
    } catch (error) {
      console.error('Error fetching additional groups:', error);
    }
  };

  // ===== Criação rápida de Grupo de Adicionais (dentro do modal de produto) =====
  const resetQuickGroup = () => {
    setShowQuickGroup(false);
    setSavingQuickGroup(false);
    setQuickGroup({ name: '', min_choices: 0, max_choices: 1, is_required: false });
    setQuickGroupItems([{ name: '', price: '' }]);
  };

  const preventEnterSubmit = (e) => {
    if (e.key === 'Enter') e.preventDefault();
  };

  const handleQuickGroupItemChange = (index, field, value) => {
    setQuickGroupItems(prev => prev.map((item, i) => (i === index ? { ...item, [field]: value } : item)));
  };

  const addQuickGroupItemRow = () => {
    setQuickGroupItems(prev => [...prev, { name: '', price: '' }]);
  };

  const removeQuickGroupItemRow = (index) => {
    setQuickGroupItems(prev => (prev.length <= 1 ? prev : prev.filter((_, i) => i !== index)));
  };

  const handleCreateQuickGroup = async () => {
    const name = (quickGroup.name || '').trim();
    if (!name) {
      toast.error('Informe o nome do grupo');
      return;
    }
    const minChoices = parseInt(quickGroup.min_choices, 10) || 0;
    const maxChoices = parseInt(quickGroup.max_choices, 10) || 0;
    if (maxChoices < 1) {
      toast.error('O máximo de escolhas deve ser pelo menos 1');
      return;
    }
    if (minChoices > maxChoices) {
      toast.error('O mínimo de escolhas não pode ser maior que o máximo');
      return;
    }

    const validItems = quickGroupItems.filter(item => (item.name || '').trim());

    setSavingQuickGroup(true);
    try {
      const groupResp = await axios.post(`${API}/additional-groups`, {
        name,
        description: '',
        min_choices: minChoices,
        max_choices: maxChoices,
        is_required: !!quickGroup.is_required
      });
      const newGroupId = groupResp.data.id;

      for (const item of validItems) {
        const price = parseFloat(String(item.price).replace(',', '.'));
        await axios.post(`${API}/additionals`, {
          name: item.name.trim(),
          description: '',
          price: Number.isNaN(price) ? 0 : price,
          group_ids: [newGroupId],
          available: true
        });
      }

      await fetchAdditionalGroups();

      setFormData(prev => {
        const current = prev.additional_groups || [];
        if (current.includes(newGroupId)) return prev;
        return { ...prev, additional_groups: [...current, newGroupId] };
      });

      toast.success(`Grupo "${name}" criado com ${validItems.length} ${validItems.length === 1 ? 'item' : 'itens'} e vinculado ao produto`);
      resetQuickGroup();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar o grupo. Tente novamente.');
    } finally {
      setSavingQuickGroup(false);
    }
  };

  const handleImageFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
      if (!validTypes.includes(file.type)) {
        alert('Formato não suportado. Use JPG, PNG ou WEBP.');
        return;
      }
      
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Arquivo muito grande. Tamanho máximo: 5MB');
        return;
      }
      
      setSelectedImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageUpload = async () => {
    if (!selectedImageFile) return;
    
    try {
      setUploadingImage(true);
      
      const formData = new FormData();
      formData.append('file', selectedImageFile);
      
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/products/upload-image`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        // Update form with uploaded image URL
        setFormData(prev => ({...prev, image_url: response.data.image_url}));
        setSelectedImageFile(null);
        alert('✅ Imagem enviada com sucesso!');
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('❌ Erro ao fazer upload: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploadingImage(false);
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const productData = {
        name: formData.name,
        description: formData.description,
        price: parseFloat(formData.price),
        category_id: formData.category_id,
        image_url: formData.image_url,
        available: formData.available,
        preparation_time: formData.preparation_time ? parseInt(formData.preparation_time) : null,
        additional_groups: formData.additional_groups || [],
        display_order: formData.display_order && formData.display_order !== '' ? parseInt(formData.display_order) : null,
        track_stock: !!formData.track_stock,
        stock_quantity: formData.track_stock && formData.stock_quantity !== '' ? parseFloat(formData.stock_quantity) : null,
        low_stock_threshold: formData.track_stock && formData.low_stock_threshold !== '' ? parseInt(formData.low_stock_threshold) : null,
        stock_unit: formData.stock_unit || 'un'
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData);
      } else {
        await axios.post(`${API}/products`, productData);
      }
      
      setIsModalOpen(false);
      setEditingProduct(null);
      setFormData({
        name: '',
        description: '',
        price: '',
        category_id: '',
        image_url: '',
        available: true,
        preparation_time: '',
        additional_groups: [],
        display_order: '',
        track_stock: false,
        stock_quantity: '',
        low_stock_threshold: '',
        stock_unit: 'un'
      });
      setSelectedImageFile(null);
      setImagePreview(null);
      setUploadingImage(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name || '',
      description: product.description || '',
      price: product.price != null ? product.price.toString() : '',
      category_id: product.category_id || '',
      image_url: product.image_url || '',
      available: product.available !== false,
      preparation_time: product.preparation_time != null ? product.preparation_time.toString() : '',
      additional_groups: product.additional_groups || [],
      display_order: product.display_order != null ? product.display_order.toString() : '',
      track_stock: !!product.track_stock,
      stock_quantity: product.stock_quantity != null ? product.stock_quantity.toString() : '',
      low_stock_threshold: product.low_stock_threshold != null ? product.low_stock_threshold.toString() : '',
      stock_unit: product.stock_unit || 'un'
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (productId) => {
    if (window.confirm('Tem certeza que deseja excluir este produto?')) {
      try {
        await axios.delete(`${API}/products/${productId}`);
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
      }
    }
  };

  const toggleAvailability = async (productId) => {
    try {
      await axios.patch(`${API}/products/${productId}/toggle-availability`);
      fetchProducts();
    } catch (error) {
      console.error('Error toggling availability:', error);
    }
  };

  // Toggle featured status of a product
  const toggleFeatured = async (productId, currentFeatured) => {
    try {
      await axios.put(`${API}/products/${productId}/featured`);
      // Update local state immediately for better UX
      setProducts(prev => prev.map(p => 
        p.id === productId ? { ...p, featured: !currentFeatured } : p
      ));
    } catch (error) {
      console.error('Error toggling featured:', error);
      alert('Erro ao atualizar produto em destaque');
    }
  };

  const openModal = () => {
    setEditingProduct(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      category_id: '',
      image_url: '',
      available: true,
      preparation_time: '',
      additional_groups: [],
      display_order: '',
      track_stock: false,
      stock_quantity: '',
      low_stock_threshold: '',
      stock_unit: 'un'
    });
    setSelectedImageFile(null);
    setImagePreview(null);
    setUploadingImage(false);
    setIsModalOpen(true);
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category ? category.name : 'Categoria não encontrada';
  };


  const handleExport = async (format = 'csv') => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/all?format=${format}`, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const extension = format.toLowerCase();
      link.setAttribute('download', `cardapio_${new Date().toISOString().split('T')[0]}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting data:', error);
      alert('Erro ao exportar dados. Verifique se você está logado.');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('Selecione um arquivo CSV');
      return;
    }

    try {
      setImporting(true);
      const formData = new FormData();
      formData.append('file', importFile);

      const response = await axios.post(`${API}/import/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setImportResult(response.data);
      setImportFile(null);
      
      // Reload data
      fetchProducts();
      fetchCategories();
      
      setTimeout(() => {
        setIsImportModalOpen(false);
        setImportResult(null);
      }, 3000);
    } catch (error) {
      console.error('Error importing data:', error);
      alert('Erro ao importar dados: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  // ===== FUNÇÕES DA GALERIA DE IMAGENS =====
  
  // Carregar imagens da galeria
  const loadGalleryImages = async (category = '', search = '') => {
    setLoadingGallery(true);
    try {
      let url = `${API}/gallery`;
      const params = new URLSearchParams();
      if (category) params.append('category', category);
      if (search) params.append('search', search);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setGalleryImages(response.data);
    } catch (error) {
      console.error('Erro ao carregar galeria:', error);
    } finally {
      setLoadingGallery(false);
    }
  };

  // Carregar categorias da galeria
  const loadGalleryCategories = async () => {
    try {
      const response = await axios.get(`${API}/gallery/categories`);
      setGalleryCategories(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
    }
  };

  // Abrir modal da galeria
  const openGalleryModal = () => {
    setShowGalleryModal(true);
    loadGalleryImages();
    loadGalleryCategories();
  };

  // Selecionar imagem da galeria
  const selectGalleryImage = (imageUrl) => {
    setFormData((prev) => ({ ...prev, image_url: imageUrl }));
    toast.success('Imagem selecionada!');
    // Adiar o fechamento da galeria pra próximo tick para garantir que:
    // 1) o state do produto já foi commitado pelo React,
    // 2) o evento de click termine de propagar sem afetar o modal pai,
    // evitando que o modal de Editar Produto feche junto.
    setTimeout(() => setShowGalleryModal(false), 0);
  };

  // Abrir modal de geração de imagem com IA
  const openAIImageModal = () => {
    setAiImageResult(null);
    setShowAIImageModal(true);
  };

  // Gerar imagem com IA (Nano Banana)
  const generateAIImage = async () => {
    if (!aiImageConfig.emergent_key) {
      toast.error('Digite sua chave Emergent Universal');
      return;
    }
    
    if (!formData.name) {
      toast.error('O produto precisa ter um nome');
      return;
    }
    
    setAiImageLoading(true);
    setAiImageResult(null);
    
    try {
      // Salvar chave se configurado
      if (aiImageConfig.remember_config) {
        localStorage.setItem('emergent_image_key', aiImageConfig.emergent_key);
      }
      
      // Encontrar nome da categoria
      const category = categories.find(c => c.id === formData.category_id);
      const categoryName = category?.name || '';
      
      const response = await axios.post(`${API}/products/generate-ai-image`, {
        product_name: formData.name,
        product_description: formData.description || '',
        category_name: categoryName,
        photo_style: aiImageConfig.photo_style,
        environment: aiImageConfig.environment,
        extra_details: aiImageConfig.extra_details,
        use_logo: aiImageConfig.use_logo,
        emergent_key: aiImageConfig.emergent_key
      });
      
      if (response.data.success && response.data.image_url) {
        setAiImageResult({
          success: true,
          image_url: response.data.image_url
        });
        toast.success('Imagem gerada com sucesso!');
      } else {
        throw new Error('Resposta inválida do servidor');
      }
    } catch (error) {
      console.error('Erro ao gerar imagem:', error);
      const errorMsg = error.response?.data?.detail || error.message || 'Erro desconhecido';
      setAiImageResult({ success: false, error: errorMsg });
      toast.error(errorMsg);
    } finally {
      setAiImageLoading(false);
    }
  };

  // Aplicar imagem gerada pela IA
  const applyAIImage = () => {
    if (aiImageResult?.image_url) {
      setFormData((prev) => ({ ...prev, image_url: aiImageResult.image_url }));
      toast.success('Imagem aplicada ao produto!');
      // Mesma técnica do selectGalleryImage: adiar o fechamento do modal
      // filho pra próximo tick para o modal de Editar Produto não fechar junto.
      setTimeout(() => {
        setShowAIImageModal(false);
        setAiImageResult(null);
      }, 0);
    }
  };

  // ========== GERAÇÃO EM LOTE ==========
  
  // Abrir modal de lote com produtos sem imagem
  const DEFAULT_PRODUCT_IMAGE = "https://res.cloudinary.com/df9ady2jx/image/upload/v1766408761/pedi-facil/default-product-image.jpg";
  
  const openBatchAIModal = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      const allProducts = response.data || [];
      const productsWithoutImage = allProducts.filter(p => 
        !p.image_url || 
        p.image_url === '' || 
        p.image_url === DEFAULT_PRODUCT_IMAGE ||
        p.image_url.includes('default-product-image')
      );
      setBatchProducts(productsWithoutImage);
      setBatchProgress({ current: 0, total: productsWithoutImage.length, status: 'idle' });
      setBatchResults([]);
      setShowBatchAIModal(true);
    } catch (error) {
      toast.error('Erro ao carregar produtos');
    }
  };

  // Gerar TODAS as imagens em massa
  const startBatchGeneration = async () => {
    if (!aiImageConfig.emergent_key) {
      toast.error('Digite sua chave Emergent Universal');
      return;
    }
    
    if (batchProducts.length === 0) {
      toast.info('Nenhum produto para processar');
      return;
    }

    // Salvar chave se configurado
    if (aiImageConfig.remember_config) {
      localStorage.setItem('emergent_image_key', aiImageConfig.emergent_key);
    }

    setBatchLoading(true);
    setBatchProgress({ current: 0, total: batchProducts.length, status: 'running' });
    setBatchResults([]);

    const results = [];
    
    for (let i = 0; i < batchProducts.length; i++) {
      const product = batchProducts[i];
      const category = categories.find(c => c.id === product.category_id);
      
      setBatchProgress(prev => ({ ...prev, current: i + 1 }));
      
      try {
        // Gerar imagem
        const response = await axios.post(`${API}/products/generate-ai-image`, {
          product_name: product.name,
          product_description: product.description || '',
          category_name: category?.name || '',
          photo_style: aiImageConfig.photo_style,
          environment: aiImageConfig.environment,
          extra_details: aiImageConfig.extra_details,
          use_logo: aiImageConfig.use_logo,
          emergent_key: aiImageConfig.emergent_key
        });
        
        if (response.data.success && response.data.image_url) {
          // Salvar APENAS a imagem no produto (PATCH parcial para não sobrescrever preços/dados)
          await axios.patch(`${API}/products/${product.id}`, {
            image_url: response.data.image_url
          });
          
          results.push({
            productId: product.id,
            name: product.name,
            success: true,
            image_url: response.data.image_url
          });
        } else {
          results.push({
            productId: product.id,
            name: product.name,
            success: false,
            error: 'Falha ao gerar imagem'
          });
        }
      } catch (error) {
        results.push({
          productId: product.id,
          name: product.name,
          success: false,
          error: error.response?.data?.detail || error.message || 'Erro desconhecido'
        });
      }
      
      // Atualizar resultados em tempo real
      setBatchResults([...results]);
      
      // Pequeno delay entre requisições para não sobrecarregar a API
      if (i < batchProducts.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    setBatchProgress(prev => ({ ...prev, status: 'done' }));
    setBatchLoading(false);
    
    const successCount = results.filter(r => r.success).length;
    if (successCount === results.length) {
      toast.success(`🎉 Todas as ${successCount} imagens foram geradas!`);
    } else {
      toast.info(`✅ ${successCount} de ${results.length} imagens geradas`);
    }
    
    // Recarregar produtos
    fetchProducts();
  };

  // Cancelar geração em massa
  const cancelBatchGeneration = () => {
    setShowBatchAIModal(false);
    setBatchLoading(false);
    setBatchProgress({ current: 0, total: 0, status: 'idle' });
  };

  // Vincular imagens automaticamente
  const autoLinkImages = async () => {
    setAutoLinkLoading(true);
    setAutoLinkResult(null);
    try {
      const response = await axios.post(`${API}/gallery/auto-link`);
      setAutoLinkResult(response.data);
      
      if (response.data.linked > 0) {
        toast.success(`${response.data.linked} produtos vinculados!`);
        fetchProducts(); // Recarregar produtos
      } else {
        toast.info('Nenhum produto foi vinculado');
      }
    } catch (error) {
      console.error('Erro ao vincular imagens:', error);
      toast.error('Erro ao vincular imagens');
    } finally {
      setAutoLinkLoading(false);
    }
  };

  // Buscar sugestões da IA para vincular imagens
  const fetchAISuggestions = async () => {
    setAiSuggestionsLoading(true);
    setAiSuggestions([]);
    setSelectedSuggestions({});
    
    try {
      const response = await axios.post(`${API}/gallery/ai-suggestions`);
      
      if (response.data.suggestions && response.data.suggestions.length > 0) {
        setAiSuggestions(response.data.suggestions);
        // Selecionar todas por padrão
        const selected = {};
        response.data.suggestions.forEach(s => {
          selected[s.product_id] = true;
        });
        setSelectedSuggestions(selected);
        setShowAISuggestionsModal(true);
      } else {
        toast.info(response.data.message || 'Nenhuma sugestão disponível');
      }
    } catch (error) {
      console.error('Erro ao buscar sugestões IA:', error);
      toast.error(error.response?.data?.detail || 'Erro ao buscar sugestões');
    } finally {
      setAiSuggestionsLoading(false);
    }
  };

  // Aplicar sugestões selecionadas
  const applyAISuggestions = async () => {
    const selectedList = aiSuggestions.filter(s => selectedSuggestions[s.product_id]);
    
    if (selectedList.length === 0) {
      toast.warning('Selecione pelo menos uma sugestão');
      return;
    }
    
    setApplyingSuggestions(true);
    
    try {
      // Garantir que cada item tem product_id e image_url
      const payload = selectedList.map(s => ({
        product_id: s.product_id,
        image_url: s.image_url
      }));
      
      console.log('Aplicando sugestões:', payload);
      
      const response = await axios.post(`${API}/gallery/apply-suggestions`, payload);
      
      console.log('Resposta:', response.data);
      
      if (response.data.applied > 0) {
        toast.success(`${response.data.applied} imagens vinculadas!`);
        // Forçar refresh dos produtos
        await fetchProducts();
        setShowAISuggestionsModal(false);
        setAiSuggestions([]);
        setSelectedSuggestions({});
      } else {
        toast.info('Nenhuma imagem foi vinculada');
      }
    } catch (error) {
      console.error('Erro ao aplicar sugestões:', error);
      toast.error('Erro ao aplicar sugestões');
    } finally {
      setApplyingSuggestions(false);
    }
  };

  // Toggle sugestão individual
  const toggleSuggestion = (productId) => {
    setSelectedSuggestions(prev => ({
      ...prev,
      [productId]: !prev[productId]
    }));
  };

  // Toggle todas as sugestões
  const toggleAllSuggestions = (checked) => {
    const newSelected = {};
    aiSuggestions.forEach(s => {
      newSelected[s.product_id] = checked;
    });
    setSelectedSuggestions(newSelected);
  };

  // ==================== MONTAR CARDÁPIO COM IA ====================
  
  // Handler para seleção de arquivos (imagens e PDFs)
  const handleMenuAIFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    const maxFiles = 10;
    
    // Filtrar arquivos válidos
    const validFiles = files.filter(f => validTypes.includes(f.type)).slice(0, maxFiles);
    
    if (validFiles.length === 0) {
      toast.error('Formatos aceitos: JPG, PNG, WEBP ou PDF');
      return;
    }
    
    // Converter para base64
    const promises = validFiles.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          resolve({
            name: file.name,
            type: file.type,
            base64: e.target.result,
            size: file.size
          });
        };
        reader.readAsDataURL(file);
      });
    });
    
    const converted = await Promise.all(promises);
    setMenuAIImages(prev => [...prev, ...converted].slice(0, maxFiles));
    toast.success(`${converted.length} arquivo(s) adicionado(s)`);
  };
  
  // Remover imagem da lista
  const removeMenuAIImage = (index) => {
    setMenuAIImages(prev => prev.filter((_, i) => i !== index));
  };
  
  // Analisar cardápio com IA
  const analyzeMenuWithAI = async () => {
    if (menuAIImages.length === 0) {
      toast.error('Adicione pelo menos uma imagem do cardápio');
      return;
    }
    
    setMenuAIAnalyzing(true);
    setMenuAIResult(null);
    
    try {
      // Extrair apenas o base64 das imagens
      const imagesBase64 = menuAIImages.map(img => img.base64);
      
      const response = await axios.post(`${API}/menu/analyze-with-ai`, {
        images_base64: imagesBase64
      });
      
      if (response.data.success) {
        setMenuAIResult(response.data.data);
        toast.success(`Encontrado: ${response.data.summary.total_produtos} produtos, ${response.data.summary.total_categorias} categorias`);
      } else {
        toast.error(response.data.error || 'Erro na análise');
      }
    } catch (error) {
      console.error('Erro na análise:', error);
      toast.error(error.response?.data?.detail || 'Erro ao analisar cardápio');
    } finally {
      setMenuAIAnalyzing(false);
    }
  };
  
  // Salvar resultado da análise
  const saveMenuAIResult = async () => {
    if (!menuAIResult) return;
    
    setMenuAISaving(true);
    
    try {
      const response = await axios.post(`${API}/menu/save-ai-analysis`, menuAIResult);
      
      if (response.data.success) {
        const c = response.data.created;
        toast.success(`Importado: ${c.categorias} categorias, ${c.produtos} produtos, ${c.adicionais} adicionais`);
        setShowMenuAIModal(false);
        setMenuAIImages([]);
        setMenuAIResult(null);
        // Recarregar dados
        fetchProducts();
        fetchCategories();
        fetchAdditionalGroups();
      }
    } catch (error) {
      console.error('Erro ao salvar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar cardápio');
    } finally {
      setMenuAISaving(false);
    }
  };
  
  // Editar item do resultado (produto, categoria, etc)
  const editMenuAIItem = (type, index, field, value) => {
    setMenuAIResult(prev => {
      const updated = { ...prev };
      if (updated[type] && updated[type][index]) {
        updated[type][index] = { ...updated[type][index], [field]: value };
      }
      return updated;
    });
  };
  
  // Remover item do resultado
  const removeMenuAIItem = (type, index) => {
    setMenuAIResult(prev => {
      const updated = { ...prev };
      if (updated[type]) {
        updated[type] = updated[type].filter((_, i) => i !== index);
      }
      return updated;
    });
  };

  // ==================== COMANDOS DE VOZ/TEXTO ====================
  
  // Processar comando de texto
  const processVoiceCommand = async (commandText = null) => {
    const command = commandText || voiceCommand.trim();
    if (!command) {
      toast.error('Digite ou fale um comando');
      return;
    }
    
    setVoiceCommandLoading(true);
    setVoiceCommandResult(null);
    
    try {
      const response = await axios.post(`${API}/products/parse-command`, {
        command: command
      });
      
      if (response.data.success) {
        setVoiceCommandResult(response.data);
        setShowVoiceConfirmModal(true);
      } else {
        toast.error(response.data.error || 'Comando não reconhecido');
      }
    } catch (error) {
      console.error('Erro ao processar comando:', error);
      toast.error(error.response?.data?.detail || 'Erro ao processar comando');
    } finally {
      setVoiceCommandLoading(false);
    }
  };
  
  // Executar comando confirmado
  const executeVoiceCommand = async () => {
    if (!voiceCommandResult) return;
    
    setVoiceCommandLoading(true);
    
    try {
      const response = await axios.post(`${API}/products/execute-command`, {
        action: voiceCommandResult.action,
        products: voiceCommandResult.products,
        changes: voiceCommandResult.changes
      });
      
      if (response.data.success) {
        // Fechar modal e limpar estado PRIMEIRO (feedback instantâneo)
        setShowVoiceConfirmModal(false);
        setVoiceCommand('');
        
        // Atualização LOCAL INSTANTÂNEA - não espera o servidor
        const affectedProducts = voiceCommandResult.products || [];
        
        if (voiceCommandResult.action === 'update_price' && affectedProducts.length > 0) {
          const newPrice = voiceCommandResult.changes?.price;
          const affectedIds = affectedProducts.map(p => p.id);
          setProducts(prev => prev.map(p => 
            affectedIds.includes(p.id) ? { ...p, price: newPrice } : p
          ));
        } else if (voiceCommandResult.action === 'toggle_availability' && affectedProducts.length > 0) {
          const newStatus = voiceCommandResult.changes?.available;
          const affectedIds = affectedProducts.map(p => p.id);
          setProducts(prev => prev.map(p => 
            affectedIds.includes(p.id) ? { ...p, available: newStatus } : p
          ));
        } else if (voiceCommandResult.action === 'delete_product' && affectedProducts.length > 0) {
          const affectedIds = affectedProducts.map(p => p.id);
          setProducts(prev => prev.filter(p => !affectedIds.includes(p.id)));
        } else if (voiceCommandResult.action === 'create_product') {
          // Para criação, precisamos buscar do servidor para ter o ID correto
          fetchProducts(); // Não espera, executa em background
        } else if (voiceCommandResult.action === 'bulk_update' && affectedProducts.length > 0) {
          const discount = voiceCommandResult.changes?.discount_percent || 0;
          const affectedIds = affectedProducts.map(p => p.id);
          setProducts(prev => prev.map(p => {
            if (affectedIds.includes(p.id)) {
              const newPrice = p.price * (1 - discount / 100);
              return { ...p, price: Math.round(newPrice * 100) / 100 };
            }
            return p;
          }));
        }
        
        // Mostrar sucesso DEPOIS da atualização visual
        toast.success(response.data.message);
        
        // Limpar resultado
        setVoiceCommandResult(null);
        
        // Sincronizar com servidor em BACKGROUND (não bloqueia a UI)
        // Isso garante consistência sem travar a interface
        setTimeout(() => {
          fetchProducts();
        }, 500);
        
      } else {
        toast.error('Erro ao executar comando');
      }
    } catch (error) {
      console.error('Erro ao executar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao executar comando');
    } finally {
      setVoiceCommandLoading(false);
    }
  };
  
  // Gravação de áudio
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000
        } 
      });
      
      // Tentar usar formato compatível (webm para Chrome, mp4 para Safari)
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') 
        ? 'audio/webm' 
        : MediaRecorder.isTypeSupported('audio/mp4') 
          ? 'audio/mp4' 
          : 'audio/ogg';
      
      const recorder = new MediaRecorder(stream, { mimeType });
      const chunks = [];
      
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      
      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: mimeType });
        stream.getTracks().forEach(track => track.stop());
        
        console.log(`🎤 Áudio gravado: ${blob.size} bytes, tipo: ${mimeType}`);
        
        // Converter para base64 e enviar para transcrição
        const reader = new FileReader();
        reader.onloadend = async () => {
          const base64 = reader.result;
          await transcribeAudio(base64);
        };
        reader.readAsDataURL(blob);
      };
      
      recorder.start(100); // Coletar dados a cada 100ms
      setMediaRecorder(recorder);
      setIsRecording(true);
      toast.info('🎤 Gravando... Fale seu comando e clique novamente para parar');
    } catch (error) {
      console.error('Erro ao gravar:', error);
      if (error.name === 'NotAllowedError') {
        toast.error('Permissão de microfone negada. Verifique as configurações do navegador.');
      } else {
        toast.error('Não foi possível acessar o microfone');
      }
    }
  };
  
  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      setMediaRecorder(null);
      toast.info('⏳ Processando áudio...');
    }
  };
  
  // Transcrever áudio usando Deepgram
  const transcribeAudio = async (audioBase64) => {
    setVoiceCommandLoading(true);
    try {
      const response = await axios.post(`${API}/transcribe-audio`, {
        audio_base64: audioBase64
      });
      
      if (response.data.success && response.data.text) {
        setVoiceCommand(response.data.text);
        toast.success(`🎤 Transcrito: "${response.data.text}"`);
        // Processar comando automaticamente
        await processVoiceCommand(response.data.text);
      } else {
        toast.error(response.data.error || 'Não foi possível transcrever o áudio');
      }
    } catch (error) {
      console.error('Erro na transcrição:', error);
      toast.error(error.response?.data?.detail || 'Erro ao transcrever áudio. Verifique se o Deepgram está configurado.');
    } finally {
      setVoiceCommandLoading(false);
    }
  };

  const downloadTemplate = () => {
    const template = `tipo,id_original,nome,descricao,preco,categoria_pai,cor,posicao,dias_disponiveis,imagem_url,disponivel,tempo_preparo,grupos_adicionais,grupo_min_escolhas,grupo_max_escolhas,grupo_obrigatorio,grupo_limite_um_por_item,adicional_grupos
categoria,,Lanches,Hambúrgueres e sanduíches artesanais,,,#ef4444,1,segunda|terça|quarta|quinta|sexta|sábado|domingo,,true,,,,,,
categoria,,Bebidas,Sucos e refrigerantes gelados,,,#3b82f6,2,segunda|terça|quarta|quinta|sexta|sábado|domingo,,true,,,,,,
categoria,,Sobremesas,Doces e sobremesas deliciosas,,,#22c55e,3,segunda|terça|quarta|quinta|sexta|sábado|domingo,,true,,,,,,
grupo_adicional,,Adicionais Hambúrguer,Extras para seu hambúrguer,,,,,,,true,,,0,5,false,false,
grupo_adicional,,Molhos,Escolha seus molhos preferidos,,,,,,,true,,,1,3,true,true,
produto,,Hambúrguer Artesanal,Pão artesanal com carne 180g e queijo cheddar,25.90,Lanches,,1,,https://unsplash.com/photo-1568901346375-23c9450c58cd,true,20,Adicionais Hambúrguer|Molhos,,,,,
produto,,X-Bacon,Hambúrguer com bacon crocante e queijo,22.90,Lanches,,2,,,true,15,Adicionais Hambúrguer,,,,,
produto,,X-Salada,Hambúrguer com alface tomate e queijo,18.90,Lanches,,3,,,true,15,,,,,,
produto,,Refrigerante Lata,Coca-Cola Pepsi ou Guaraná 350ml,5.00,Bebidas,,,,,true,,,,,,
produto,,Suco Natural,Suco de laranja ou limão feito na hora,8.00,Bebidas,,,,,true,5,,,,,
produto,,Água Mineral,Água mineral sem gás 500ml,3.50,Bebidas,,,,,true,,,,,,
produto,,Pudim,Pudim de leite condensado caseiro,12.90,Sobremesas,,,,,true,,,,,,
produto,,Brownie,Brownie de chocolate com sorvete,15.90,Sobremesas,,,,,true,10,,,,,
adicional,,Bacon Extra,Porção extra de bacon crocante,3.50,,,,,,true,,,,,,,Adicionais Hambúrguer
adicional,,Queijo Extra,Fatia adicional de queijo cheddar,2.50,,,,,,true,,,,,,,Adicionais Hambúrguer
adicional,,Ovo,Ovo frito,2.00,,,,,,true,,,,,,,Adicionais Hambúrguer
adicional,,Ketchup,Molho ketchup,0.00,,,,,,true,,,,,,,Molhos
adicional,,Maionese,Molho maionese especial,0.00,,,,,,true,,,,,,,Molhos

INSTRUÇÕES DE PREENCHIMENTO (Versão Completa):
==============================================

TIPOS DISPONÍVEIS:
- categoria: Define uma categoria de produtos
- grupo_adicional: Define um grupo de adicionais (ex: "Escolha o molho")
- produto: Produtos do cardápio
- adicional: Itens extras que podem ser adicionados aos produtos

CAMPOS POR TIPO:

1. CATEGORIA:
   - nome: Nome da categoria (obrigatório)
   - descricao: Descrição opcional
   - cor: Código hexadecimal (ex: #ef4444)
   - posicao: Ordem de exibição (1, 2, 3...)
   - dias_disponiveis: Dias da semana separados por | (ex: segunda|terça|quarta)
   - disponivel: true ou false

2. GRUPO_ADICIONAL:
   - nome: Nome do grupo (obrigatório)
   - descricao: Descrição opcional
   - grupo_min_escolhas: Mínimo de adicionais obrigatórios (0 = opcional)
   - grupo_max_escolhas: Máximo de adicionais permitidos
   - grupo_obrigatorio: true se o cliente deve escolher pelo menos o mínimo
   - grupo_limite_um_por_item: true se só pode escolher 1 unidade de cada

3. PRODUTO:
   - nome: Nome do produto (obrigatório)
   - descricao: Descrição
   - preco: Preço (obrigatório, use ponto como decimal)
   - categoria_pai: Nome exato da categoria (obrigatório)
   - posicao: Ordem de exibição
   - imagem_url: URL da imagem
   - disponivel: true ou false
   - tempo_preparo: Em minutos
   - grupos_adicionais: Nomes dos grupos separados por | (ex: Molhos|Extras)

4. ADICIONAL:
   - nome: Nome do adicional (obrigatório)
   - descricao: Descrição
   - preco: Preço do adicional (obrigatório)
   - disponivel: true ou false
   - adicional_grupos: Nomes dos grupos a que pertence separados por |

ORDEM DE IMPORTAÇÃO (importante):
1. Primeiro: Categorias
2. Segundo: Grupos de Adicionais
3. Terceiro: Produtos
4. Quarto: Adicionais

DICAS:
- Use o EXPORT para gerar um backup completo do cardápio
- O id_original é apenas para referência (não precisa preencher)
- Separe múltiplos valores com | (pipe)
- Não use vírgulas dentro dos campos`;

    const blob = new Blob([template], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'template_importacao.csv');
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleCreateCategoryInline = async (e) => {
    e.preventDefault();
    if (!newCategoryData.name.trim()) {
      alert('Por favor, insira o nome da categoria');
      return;
    }
    
    try {
      setCreatingCategory(true);
      const response = await axios.post(`${API}/categories`, newCategoryData);
      const newCategory = response.data;
      
      // Atualizar lista de categorias
      await fetchCategories();
      
      // Selecionar automaticamente a nova categoria
      setFormData({...formData, category_id: newCategory.id});
      
      // Resetar e fechar modal
      setNewCategoryData({ name: '', description: '', color: '#3b82f6' });
      setIsCreateCategoryModalOpen(false);
    } catch (error) {
      console.error('Error creating category:', error);
      alert('Erro ao criar categoria: ' + (error.response?.data?.detail || error.message));
    } finally {
      setCreatingCategory(false);
    }
  };

  const openCreateCategoryModal = () => {
    setNewCategoryData({ name: '', description: '', color: '#3b82f6' });
    setIsCreateCategoryModalOpen(true);
  };

  const handleCreateDemoData = async () => {
    if (!window.confirm('Deseja criar dados demonstrativos?\n\nIsso criará:\n• 4 categorias (Hambúrgueres, Pizzas, Bebidas, Sobremesas)\n• 8 produtos de exemplo\n• 3 grupos de adicionais\n\nATENÇÃO: Só funciona se o restaurante ainda não tiver categorias.')) {
      return;
    }

    try {
      setCreatingDemo(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/demo/populate`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.success) {
        alert(`✅ ${response.data.message}\n\n` +
              `• ${response.data.created.categories} categorias\n` +
              `• ${response.data.created.products} produtos\n` +
              `• ${response.data.created.additional_groups} grupos de adicionais`);
        
        // Recarregar dados
        await fetchProducts();
        await fetchCategories();
        await fetchAdditionalGroups();
      }
    } catch (error) {
      console.error('Error creating demo data:', error);
      alert('❌ Erro ao criar dados demonstrativos:\n' + (error.response?.data?.detail || error.message));
    } finally {
      setCreatingDemo(false);
    }
  };


  if (loading) {
    return (
      <div className="glass-card">
        <div className="flex items-center justify-center py-12">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header - Responsivo */}
      <ProductsToolbar
        aiSuggestionsLoading={aiSuggestionsLoading}
        autoLinkImages={autoLinkImages}
        autoLinkLoading={autoLinkLoading}
        categories={categories}
        downloadTemplate={downloadTemplate}
        fetchAISuggestions={fetchAISuggestions}
        handleExport={handleExport}
        isRecording={isRecording}
        openBatchAIModal={openBatchAIModal}
        openModal={openModal}
        processVoiceCommand={processVoiceCommand}
        products={products}
        selectedCategoryFilter={selectedCategoryFilter}
        setIsImportModalOpen={setIsImportModalOpen}
        setSelectedCategoryFilter={setSelectedCategoryFilter}
        setShowMenuAIModal={setShowMenuAIModal}
        setShowProductImages={setShowProductImages}
        setVoiceCommand={setVoiceCommand}
        showProductImages={showProductImages}
        startRecording={startRecording}
        stopRecording={stopRecording}
        voiceCommand={voiceCommand}
        voiceCommandLoading={voiceCommandLoading}
      />

      {/* Grid de Produtos - Responsivo Otimizado */}
      <ProductsGrid
        getCategoryName={getCategoryName}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        openModal={openModal}
        products={products}
        selectedCategoryFilter={selectedCategoryFilter}
        showProductImages={showProductImages}
        toggleAvailability={toggleAvailability}
        toggleFeatured={toggleFeatured}
      />

      {/* Modal principal do Produto -> components/content/ProductModal.js */}
      <ProductModal
        isModalOpen={isModalOpen}
        showGalleryModal={showGalleryModal}
        showAIImageModal={showAIImageModal}
        showBatchAIModal={showBatchAIModal}
        setIsModalOpen={setIsModalOpen}
        editingProduct={editingProduct}
        handleSubmit={handleSubmit}
        formData={formData}
        setFormData={setFormData}
        categories={categories}
        openCreateCategoryModal={openCreateCategoryModal}
        handleImageFileSelect={handleImageFileSelect}
        imagePreview={imagePreview}
        selectedImageFile={selectedImageFile}
        handleImageUpload={handleImageUpload}
        uploadingImage={uploadingImage}
        openGalleryModal={openGalleryModal}
        openAIImageModal={openAIImageModal}
        showQuickGroup={showQuickGroup}
        setShowQuickGroup={setShowQuickGroup}
        quickGroup={quickGroup}
        setQuickGroup={setQuickGroup}
        preventEnterSubmit={preventEnterSubmit}
        quickGroupItems={quickGroupItems}
        handleQuickGroupItemChange={handleQuickGroupItemChange}
        removeQuickGroupItemRow={removeQuickGroupItemRow}
        addQuickGroupItemRow={addQuickGroupItemRow}
        resetQuickGroup={resetQuickGroup}
        handleCreateQuickGroup={handleCreateQuickGroup}
        savingQuickGroup={savingQuickGroup}
        additionalGroups={additionalGroups}
      />

      
      {/* Import Modal -> components/content/ImportModal.js */}
      <ImportModal isImportModalOpen={isImportModalOpen} setIsImportModalOpen={setIsImportModalOpen} importFile={importFile} setImportFile={setImportFile} importResult={importResult} setImportResult={setImportResult} importing={importing} handleImport={handleImport} downloadTemplate={downloadTemplate} />

      {/* Modal Vincular Imagens Automaticamente -> components/content/AutoLinkModal.js */}
      <AutoLinkModal showAutoLinkModal={showAutoLinkModal} setShowAutoLinkModal={setShowAutoLinkModal} autoLinkImages={autoLinkImages} autoLinkLoading={autoLinkLoading} autoLinkResult={autoLinkResult} setAutoLinkResult={setAutoLinkResult} />

      {/* Modal Gerar Imagem com IA -> components/content/AIImageModal.js */}
      <AIImageModal showAIImageModal={showAIImageModal} setShowAIImageModal={setShowAIImageModal} aiImageConfig={aiImageConfig} setAiImageConfig={setAiImageConfig} aiImageLoading={aiImageLoading} aiImageResult={aiImageResult} generateAIImage={generateAIImage} applyAIImage={applyAIImage} formData={formData} categories={categories} loading={loading} />

      {/* Modal Geracao em Lote -> components/content/BatchAIModal.js */}
      <BatchAIModal showBatchAIModal={showBatchAIModal} setShowBatchAIModal={setShowBatchAIModal} batchLoading={batchLoading} batchProducts={batchProducts} batchProgress={batchProgress} batchResults={batchResults} startBatchGeneration={startBatchGeneration} cancelBatchGeneration={cancelBatchGeneration} aiImageConfig={aiImageConfig} setAiImageConfig={setAiImageConfig} categories={categories} loading={loading} />

      {/* Modal Galeria de Imagens -> components/content/GalleryModal.js */}
      <GalleryModal showGalleryModal={showGalleryModal} setShowGalleryModal={setShowGalleryModal} galleryImages={galleryImages} galleryCategories={galleryCategories} galleryFilter={galleryFilter} setGalleryFilter={setGalleryFilter} gallerySearch={gallerySearch} setGallerySearch={setGallerySearch} loadGalleryImages={loadGalleryImages} loadingGallery={loadingGallery} selectGalleryImage={selectGalleryImage} loading={loading} />

      {/* Create Category Inline Modal -> components/content/CreateCategoryModal.js */}
      <CreateCategoryModal isCreateCategoryModalOpen={isCreateCategoryModalOpen} setIsCreateCategoryModalOpen={setIsCreateCategoryModalOpen} newCategoryData={newCategoryData} setNewCategoryData={setNewCategoryData} creatingCategory={creatingCategory} handleCreateCategoryInline={handleCreateCategoryInline} COLOR_OPTIONS={COLOR_OPTIONS} />

      {/* Modal de Sugestoes IA -> components/content/AISuggestionsModal.js */}
      <AISuggestionsModal showAISuggestionsModal={showAISuggestionsModal} setShowAISuggestionsModal={setShowAISuggestionsModal} aiSuggestions={aiSuggestions} applyAISuggestions={applyAISuggestions} applyingSuggestions={applyingSuggestions} selectedSuggestions={selectedSuggestions} toggleAllSuggestions={toggleAllSuggestions} toggleSuggestion={toggleSuggestion} />

      {/* Modal de Confirmacao de Comando de Voz/Texto -> components/content/VoiceConfirmModal.js */}
      <VoiceConfirmModal showVoiceConfirmModal={showVoiceConfirmModal} setShowVoiceConfirmModal={setShowVoiceConfirmModal} voiceCommandResult={voiceCommandResult} setVoiceCommandResult={setVoiceCommandResult} voiceCommandLoading={voiceCommandLoading} executeVoiceCommand={executeVoiceCommand} products={products} />

      {/* Modal Montar Cardápio com IA -> components/content/MenuAIModal.js */}
      <MenuAIModal showMenuAIModal={showMenuAIModal} setShowMenuAIModal={setShowMenuAIModal} menuAIResult={menuAIResult} setMenuAIResult={setMenuAIResult} menuAIImages={menuAIImages} setMenuAIImages={setMenuAIImages} menuAIAnalyzing={menuAIAnalyzing} menuAISaving={menuAISaving} handleMenuAIFileSelect={handleMenuAIFileSelect} removeMenuAIImage={removeMenuAIImage} analyzeMenuWithAI={analyzeMenuWithAI} editMenuAIItem={editMenuAIItem} removeMenuAIItem={removeMenuAIItem} saveMenuAIResult={saveMenuAIResult} />

    </div>
  );
};


export default ProductsContent;
